import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import moment from 'moment';
import {
  sanitizeS3PhotoUrl,
  getCandidateProfilePhotoUrl,
  getCandidateSurveyPhotosUrls,
  getCandidateAllPhotoUrls,
  extractAllCandidateS3Urls
} from '../../../utils/s3PhotoUtils';

// Helper to resolve age from dob
const calculateAge = (row) => {
  if (!process.env.REACT_APP_HUMBLEBEE_API_URL?.includes('devapi.thehumblebee.co')) {
    const age = row?.surveyData?.stages?.[0]?.form_data?.dob ?? '';
    if (age) {
      const dob = new Date(age);
      const diff_ms = Date.now() - dob.getTime();
      const age_dt = new Date(diff_ms);
      return Math.abs(age_dt.getUTCFullYear() - 1970);
    }
  }
  return '-';
};

const getApiaryPhotosUrls = (row) => {
  const allUrls = extractAllCandidateS3Urls(row);
  const apiaryUrls = allUrls.filter(u =>
    u.toLowerCase().includes('apiary') || u.toLowerCase().includes('hive') || u.toLowerCase().includes('box') || u.toLowerCase().includes('bee')
  );
  return apiaryUrls.length > 0 ? apiaryUrls.join(' | ') : '-';
};

// Helper to safely extract a field value from a specific survey stage
const getSurveyFieldValue = (row, stageName, fieldName) => {
  const stages = row.surveyData?.stages;
  if (!Array.isArray(stages)) return '';
  const stageObj = stages.find(s => s.stage_name === stageName);
  return stageObj?.form_data?.[fieldName] || '';
};

// Available Columns with Photo S3 URLs
const AVAILABLE_COLUMNS = [
  { label: 'Name', key: 'name', path: 'userInfo.name' },
  {
    label: 'Surveyed By',
    key: 'surveyorName',
    formatter: (row) => row.surveyorName || row.surveyData?.created_by?.name || row.created_by_user?.name || '-'
  },
  { label: 'Stage', key: 'stage', path: 'stage', formatter: (row) => row.stage === 'advance' ? 'Advanced' : 'Basic' },
  {
    label: 'Gender',
    key: 'gender',
    formatter: (row) => {
      const cell = row.userInfo?.gender;
      if (!cell) return '-';
      return cell.charAt(0).toUpperCase() + cell.slice(1).toLowerCase();
    }
  },
  { label: 'Age', key: 'age', formatter: (row) => calculateAge(row) },
  { label: 'Mobile No.', key: 'mobileNumber', path: 'userInfo.mobileNumber' },
  {
    label: 'State',
    key: 'state',
    formatter: (row) => {
      return row.userInfo?.address?.state || row.surveyData?.stages?.[0]?.form_data?.state || row.state || '-';
    }
  },
  {
    label: 'District',
    key: 'district',
    formatter: (row) => {
      return row.userInfo?.address?.district || row.surveyData?.stages?.[0]?.form_data?.district || row.district || '-';
    }
  },
  {
    label: 'Village',
    key: 'village',
    formatter: (row) => {
      return row.userInfo?.address?.village || row.surveyData?.stages?.[0]?.form_data?.village || '-';
    }
  },
  {
    label: 'Block',
    key: 'block',
    formatter: (row) => {
      return row.userInfo?.address?.block || row.surveyData?.stages?.[0]?.form_data?.block || '-';
    }
  },
  {
    label: 'Aadhaar Number',
    key: 'aadharNumber',
    formatter: (row) => {
      const aadhar = row.aadhar_number || row.surveyData?.stages?.[0]?.form_data?.aadharNumber;
      return aadhar ? aadhar.toString() : '-';
    }
  },
  {
    label: 'SRLM Associated',
    key: 'srlm_associated',
    formatter: (row) => {
      if (row.srlm_associated === true) return 'Yes';
      if (row.srlm_associated === false) return 'No';
      return '-';
    }
  },
  {
    label: 'Apiary Status',
    key: 'apiaryStatus',
    formatter: (row) => row.apiaryStatus || 'Absent'
  },
  {
    label: 'Project Name',
    key: 'projectName',
    formatter: (row) => row.projectName || 'No Cluster Assigned'
  },
  {
    label: 'Cluster Name',
    key: 'clusterName',
    formatter: (row) => row.clusterName || 'No Cluster Assigned'
  },
  {
    label: 'Cluster Assigned',
    key: 'clusterAssigned',
    formatter: (row) => (row.fieldViewData?.[0]?.cluster_id ? 'Yes' : 'No')
  },
  {
    label: 'App Installed',
    key: 'appInstalled',
    formatter: (row) => (row.userInfo?.logged_in ? 'Yes' : 'No')
  },
  {
    label: 'User Added On',
    key: 'userAddedOn',
    formatter: (row) => {
      const d = row.userInfo?.addedAt;
      return d ? moment(d).format('DD/MM/YYYY hh:mm A') : '-';
    }
  },
  {
    label: 'Added On',
    key: 'addedOn',
    formatter: (row) => {
      const d = row.fieldViewData?.[0]?.created_at;
      return d ? moment(d).format('DD/MM/YYYY hh:mm A') : '-';
    }
  },
  {
    label: 'Updated On',
    key: 'updatedOn',
    formatter: (row) => {
      const d = row.fieldViewData?.[0]?.updated_at;
      return d ? moment(d).format('DD/MM/YYYY hh:mm A') : '-';
    }
  },
  {
    label: 'Education Level',
    key: 'education',
    formatter: (row) => getSurveyFieldValue(row, 'candidateProfile', 'education') || row.userInfo?.education || '-'
  },
  {
    label: 'Land Ownership',
    key: 'landOwnerShip',
    formatter: (row) => getSurveyFieldValue(row, 'financialOverview', 'landOwnership') || row.landOwnerShip || '-'
  },
  {
    label: 'Practice Beekeeping',
    key: 'practiceBeekeeping',
    formatter: (row) => getSurveyFieldValue(row, 'beekeepingScope', 'practiceBeekeeping') || '-'
  },
  {
    label: 'Trained in Beekeeping',
    key: 'trainedinBeekeepingProgram',
    formatter: (row) => getSurveyFieldValue(row, 'beekeepingScope', 'trainedinBeekeepingProgram') || '-'
  },
  {
    label: 'Interested in More Bee Boxes',
    key: 'interestedinMoreBeeBoxes',
    formatter: (row) => getSurveyFieldValue(row, 'beekeepingScope', 'interestedinMoreBeeBoxes') || '-'
  },
  {
    label: 'Use Chemical Pesticides',
    key: 'useChemical',
    formatter: (row) => getSurveyFieldValue(row, 'cultivationDetails', 'useOfPesticides') || '-'
  },
  {
    label: 'Livestock Owner',
    key: 'ownCattle',
    formatter: (row) => getSurveyFieldValue(row, 'financialOverview', 'ownCattles') || '-'
  },
  {
    label: 'Profile Photo (S3 URL)',
    key: 'profilePhotoUrl',
    formatter: (row) => getCandidateProfilePhotoUrl(row)
  },
  {
    label: 'Survey Photos (S3 URLs)',
    key: 'surveyPhotoUrls',
    formatter: (row) => getCandidateSurveyPhotosUrls(row)
  },
  {
    label: 'Apiary & Hive Photos (S3 URLs)',
    key: 'apiaryPhotoUrls',
    formatter: (row) => getApiaryPhotosUrls(row)
  },
  {
    label: 'All Photo S3 Links',
    key: 'allPhotoUrls',
    formatter: (row) => getCandidateAllPhotoUrls(row)
  }
];

export default function ExportCSVModal({ show, onHide, data }) {
  const [selectedFields, setSelectedFields] = useState(
    AVAILABLE_COLUMNS.reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {})
  );

  useEffect(() => {
    if (show && Array.isArray(data) && data.length > 0) {
      setSelectedFields(
        AVAILABLE_COLUMNS.reduce((acc, col) => {
          acc[col.key] = true;
          return acc;
        }, {})
      );
    }
  }, [show, data]);

  const handleToggle = (key) => {
    setSelectedFields(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSelectAll = () => {
    setSelectedFields(
      AVAILABLE_COLUMNS.reduce((acc, col) => {
        acc[col.key] = true;
        return acc;
      }, {})
    );
  };

  const handleClearAll = () => {
    setSelectedFields(
      AVAILABLE_COLUMNS.reduce((acc, col) => {
        acc[col.key] = false;
        return acc;
      }, {})
    );
  };

  const triggerExport = () => {
    const activeColumns = AVAILABLE_COLUMNS.filter(col => selectedFields[col.key]);

    if (activeColumns.length === 0) {
      alert("Please select at least one field to export.");
      return;
    }

    const headers = ['S.No.', ...activeColumns.map(col => col.label)]
      .map(header => `"${header.replace(/"/g, '""')}"`)
      .join(',');

    const rows = data.map((row, index) => {
      const snVal = `"${index + 1}"`;
      const rowVals = activeColumns.map(col => {
        let val = '';
        if (col.formatter) {
          val = col.formatter(row, index);
        } else if (col.path) {
          val = col.path.split('.').reduce((acc, part) => acc && acc[part], row);
        }
        const cellString = (val !== null && val !== undefined) ? String(val) : '';
        return `"${cellString.replace(/"/g, '""')}"`;
      });
      return [snVal, ...rowVals].join(',');
    });

    const csvContent = [headers, ...rows].join('\n');
    const blob = new Blob(
      [new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent],
      { type: 'text/csv;charset=utf-8;' }
    );

    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `leads_photos_export_${new Date().getTime()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ borderBottom: '1px solid #dee2e6' }}>
        <Modal.Title style={{ color: '#ef5a20', fontWeight: 'bold' }}>
          Export Leads to CSV (Including Photo S3 Links)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '20px 30px' }}>
        <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
          Select the fields you would like to include in the exported CSV file. A total of <strong>{data.length}</strong> leads will be exported based on your active filters.
        </div>

        <div style={{ marginBottom: '20px' }}>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSelectAll}
            style={{ marginRight: '10px', backgroundColor: '#ef5a20', borderColor: '#ef5a20' }}
          >
            Select All
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleClearAll}
          >
            Clear All
          </Button>
        </div>

        <Row style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {AVAILABLE_COLUMNS.map((col) => (
            <Col md={6} key={col.key} style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                background: selectedFields[col.key] ? '#fff7ed' : '#fff'
              }}>
                <input
                  type="checkbox"
                  id={`check-${col.key}`}
                  checked={!!selectedFields[col.key]}
                  onChange={() => handleToggle(col.key)}
                  style={{ marginRight: '10px', width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label
                  htmlFor={`check-${col.key}`}
                  style={{ margin: 0, fontSize: '14px', fontWeight: col.key.includes('Photo') ? '700' : '500', color: col.key.includes('Photo') ? '#d97706' : '#334155', cursor: 'pointer' }}
                >
                  {col.label} {col.key.includes('Photo') && '📸'}
                </label>
              </div>
            </Col>
          ))}
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={triggerExport}
          style={{ backgroundColor: '#ef5a20', borderColor: '#ef5a20' }}
        >
          📥 Download CSV with Photo Links
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
